import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Footer from '../components/Footer';

type BrowserName = 'chrome' | 'firefox' | 'edge' | 'safari';

type BrowserGuide = {
  label: string;
  storeLabel: string;
  addActionLabel: string;
  accentClass: string;
  iconSrc: string;
  installSteps: string[];
  pinSteps: string[];
  note?: string;
};

const EXTENSION_STORE_URLS: Record<BrowserName, string> = {
  chrome: 'https://chromewebstore.google.com/',
  firefox: 'https://addons.mozilla.org/firefox/extensions/',
  edge: 'https://microsoftedge.microsoft.com/addons/Microsoft-Edge-Extensions-Home',
  safari: 'https://apps.apple.com/us/charts/mac-apps/safari-extensions/12016'
};

const BROWSER_GUIDES: Record<BrowserName, BrowserGuide> = {
  chrome: {
    label: 'Google Chrome',
    storeLabel: 'Chrome Web Store',
    addActionLabel: 'Add to Chrome',
    accentClass: 'text-blue-400',
    iconSrc: '/images/browser-icons/chrome.png',
    installSteps: [
      'Click "Get Extension" to open the Chrome Web Store in a new tab.',
      'Find AdSmash and click "Add to Chrome".',
      'Approve the browser prompt to finish installation.'
    ],
    pinSteps: [
      'Click the Extensions icon (puzzle piece) in the top-right toolbar.',
      'Find AdSmash in the list and click the pin icon.',
      'Confirm the AdSmash icon appears in your toolbar.'
    ]
  },
  edge: {
    label: 'Microsoft Edge',
    storeLabel: 'Microsoft Edge Add-ons',
    addActionLabel: 'Get',
    accentClass: 'text-cyan-400',
    iconSrc: '/images/browser-icons/edge.png',
    installSteps: [
      'Click "Get Extension" to open the Microsoft Edge Add-ons store in a new tab.',
      'Find AdSmash and click "Get".',
      'Approve the browser prompt to finish installation.'
    ],
    pinSteps: [
      'Click the Extensions icon (puzzle piece) in the top-right toolbar.',
      'Find AdSmash and click "Show in toolbar" or the pin icon.',
      'Confirm the AdSmash icon appears in your toolbar.'
    ]
  },
  firefox: {
    label: 'Mozilla Firefox',
    storeLabel: 'Firefox Add-ons',
    addActionLabel: 'Add to Firefox',
    accentClass: 'text-orange-300',
    iconSrc: '/images/browser-icons/firefox.png',
    installSteps: [
      'Click "Get Extension" to open Firefox Add-ons in a new tab.',
      'Find AdSmash and click "Add to Firefox".',
      'Approve the browser prompt to finish installation.'
    ],
    pinSteps: [
      'Click the Extensions icon (puzzle piece) in the top-right toolbar.',
      'Open AdSmash options from the extension item and choose "Pin to Toolbar".',
      'Confirm the AdSmash icon appears in your toolbar.'
    ]
  },
  safari: {
    label: 'Safari (macOS)',
    storeLabel: 'Mac App Store (Safari Extensions)',
    addActionLabel: 'Get',
    accentClass: 'text-slate-200',
    iconSrc: '/images/browser-icons/safari.png',
    installSteps: [
      'Click "Get Extension" to open the Mac App Store Safari Extensions page.',
      'Install the AdSmash Safari app/extension from the App Store.',
      'Open Safari and go to Safari > Settings > Extensions, then enable AdSmash.'
    ],
    pinSteps: [
      'Click the Extensions button in Safari toolbar to access enabled extensions.',
      'If the AdSmash icon is hidden, customize Safari toolbar and keep it visible.',
      'Confirm AdSmash is enabled before browsing.'
    ]
  }
};

function detectBrowser(): BrowserName {
  const ua = window.navigator.userAgent || '';

  if (/Edg\//i.test(ua)) return 'edge';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Safari\//i.test(ua) && !/Chrome|CriOS|Edg\//i.test(ua)) return 'safari';
  return 'chrome';
}

function isBrowserName(value: string | undefined): value is BrowserName {
  return value === 'chrome' || value === 'firefox' || value === 'edge' || value === 'safari';
}

const PostSignup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { browser } = useParams<{ browser?: string }>();
  const [selectedBrowser, setSelectedBrowser] = useState<BrowserName>(detectBrowser());
  const [showProceedButton, setShowProceedButton] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);

  const browserGuide = BROWSER_GUIDES[selectedBrowser];

  useEffect(() => {
    const normalizedParam = browser?.toLowerCase();
    if (isBrowserName(normalizedParam)) {
      if (selectedBrowser !== normalizedParam) {
        setSelectedBrowser(normalizedParam);
      }
      return;
    }

    const detected = detectBrowser();
    const canonicalPath = `/install/${detected}`;
    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
    if (selectedBrowser !== detected) {
      setSelectedBrowser(detected);
    }
  }, [browser, location.pathname, navigate, selectedBrowser]);

  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        window.clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const handleGetExtension = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const targetUrl = EXTENSION_STORE_URLS[selectedBrowser] || EXTENSION_STORE_URLS.chrome;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');

    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current);
    }

    setShowProceedButton(false);
    setCountdownSeconds(10);

    countdownIntervalRef.current = window.setInterval(() => {
      setCountdownSeconds((current) => {
        if (current === null) return null;
        if (current <= 1) {
          if (countdownIntervalRef.current) {
            window.clearInterval(countdownIntervalRef.current);
          }
          setShowProceedButton(true);
          return null;
        }
        return current - 1;
      });
    }, 1000);
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ 
        background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)'
      }}
    >
      {/* Header with Logo */}
      <header className="w-full px-6 md:px-12 lg:px-20 py-6 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="AdSmash Logo" className="h-10 md:h-12" />
        </Link>
      </header>

      {/* Main Content - Two Column Layout */}
      <main className="flex-1 flex flex-col md:flex-row gap-8 px-6 md:px-12 lg:px-20 py-8 md:py-12">
        {/* Left Section - Content */}
        <div className="w-full md:w-1/2 flex flex-col justify-center">
          {/* Main Title */}
          <h1 
            className="font-bold text-white mb-8 md:mb-12 leading-tight"
            style={{ fontSize: '2rem' }}
          >
            Let's set up AdSmash on your browser.
          </h1>



          <div className="rounded-2xl p-5 bg-white/5 border border-white/10 mb-6">
            <p className="text-sm text-white/60 mb-1">Selected browser</p>
            <div className="flex items-center gap-3">
              <img
                src={browserGuide.iconSrc}
                alt={`${browserGuide.label} icon`}
                className="w-8 h-8 rounded-md"
              />
              <h2 className={`font-semibold ${browserGuide.accentClass}`} style={{ fontSize: '1.1rem' }}>
                {browserGuide.label}
              </h2>
            </div>
            <p className="text-white/70 text-sm mt-2">
              Store: {browserGuide.storeLabel}
            </p>
          </div>

          {/* Step 1: Install */}
          <div className="mb-8">
            <h2 className="font-semibold mb-4" style={{ fontSize: '1.15rem' }}>
              <span className="text-blue-400">STEP #1</span>{' '}
              <span className="text-white">Install AdSmash</span>
            </h2>
            <ul className="space-y-3 mb-4">
              {browserGuide.installSteps.map((step) => (
                <li key={step} className="flex items-start gap-3 text-white">
                  <img
                    src="/images/tick.png"
                    alt="Checkmark"
                    className="flex-shrink-0 mt-0.5"
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <span style={{ fontSize: '0.98rem' }}>{step}</span>
                </li>
              ))}
            </ul>
            <p className="text-white/60 text-sm">
              Look for this button in store: <span className="text-white/90 font-semibold">{browserGuide.addActionLabel}</span>
            </p>
          </div>

          {/* Step 2: Pin/Enable */}
          <div className="mb-8">
            <h2 className="font-semibold mb-4" style={{ fontSize: '1.15rem' }}>
              <span className="text-blue-400">STEP #2</span>{' '}
              <span className="text-white">Pin or enable in browser toolbar</span>
            </h2>
            <ul className="space-y-3">
              {browserGuide.pinSteps.map((step) => (
                <li key={step} className="flex items-start gap-3 text-white">
                  <img
                    src="/images/tick.png"
                    alt="Checkmark"
                    className="flex-shrink-0 mt-0.5"
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <span style={{ fontSize: '0.98rem' }}>{step}</span>
                </li>
              ))}
            </ul>
            {browserGuide.note && (
              <p className="text-white/60 text-sm mt-4">{browserGuide.note}</p>
            )}
          </div>

          {/* Download Extension Button */}
          <button
            type="button"
            onClick={handleGetExtension}
            className="btn-adsmash-primary text-white px-8 py-4 rounded-md font-semibold transition-all shadow-lg w-full md:w-auto self-start"
            style={{ fontSize: '1rem' }}
          >
            Get Extension for {browserGuide.label}
          </button>

          {countdownSeconds !== null && !showProceedButton && (
            <p className="mt-4 text-sm text-white/80">
              Please wait {countdownSeconds}s...
            </p>
          )}

          {showProceedButton && (
            <button
              type="button"
              onClick={() => navigate('/welcome')}
              className="mt-4 border border-white/20 text-white px-8 py-4 rounded-md font-semibold transition-all hover:bg-white/10 w-full md:w-auto self-start"
              style={{ fontSize: '1rem' }}
            >
              I have downloaded and proceed
            </button>
          )}
        </div>

        {/* Right Section - Visual Guide */}
        <div className="w-full md:w-1/2 flex items-center justify-center">
          <div 
            className="w-full h-full min-h-[400px] rounded-3xl p-8 flex flex-col justify-center border border-white/10"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.04)',
              maxHeight: '600px'
            }}
          >
            <p className="text-white/60 text-sm uppercase tracking-wider mb-4">Quick Browser Guide</p>
            <div className="flex items-center gap-3 mb-4">
              <img
                src={browserGuide.iconSrc}
                alt={`${browserGuide.label} icon`}
                className="w-9 h-9 rounded-md"
              />
              <h3 className={`font-bold ${browserGuide.accentClass}`} style={{ fontSize: '1.5rem' }}>
                {browserGuide.label}
              </h3>
            </div>

            <div className="space-y-3 mb-6">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <p className="text-white text-sm font-semibold mb-1">Install</p>
                <p className="text-white/70 text-sm">
                  Open {browserGuide.storeLabel} and click <span className="text-white">{browserGuide.addActionLabel}</span>.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <p className="text-white text-sm font-semibold mb-1">Pin/Enable</p>
                <p className="text-white/70 text-sm">
                  Keep AdSmash visible in toolbar for quick on/off and site controls.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-4">
              <p className="text-blue-200 text-sm">
                After install, return here and click <span className="font-semibold">I have downloaded and proceed</span>.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PostSignup;

